import React, { useState, useEffect, useCallback } from 'react';
import { functions } from '../firebase/config';
import { httpsCallable } from 'firebase/functions';
import Header from '../components/Header';
import ConfirmationModal from '../components/modals/ConfirmationModal';

const SuperAdminPage = ({ navigateTo, currentPage }) => {
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
    const [userToReset, setUserToReset] = useState(null);

    const fetchUsers = useCallback(async () => {
        setIsLoading(true);
        setError('');
        try {
            const getAllUsers = httpsCallable(functions, 'getAllUsers');
            const result = await getAllUsers();
            setUsers(result.data);
        } catch (err) {
            console.error("Error fetching users:", err);
            setError(err.message || "Could not fetch users.");
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleAdminToggle = async (targetUid, currentStatus) => {
        try {
            const setSuperAdminStatus = httpsCallable(functions, 'setSuperAdminStatus');
            await setSuperAdminStatus({ targetUid, makeAdmin: !currentStatus });
            fetchUsers();
        } catch (err) {
            console.error("Error updating admin status:", err);
            setError(err.message || "Could not update admin status.");
        }
    };

    const handleResetWelcome = async (targetUid) => {
        if (!window.confirm("Are you sure you want to reset this user's welcome screen? They will be prompted to set up their profile again on next login.")) {
            return;
        }
        try {
            const resetUserProfile = httpsCallable(functions, 'resetUserProfile');
            await resetUserProfile({ targetUid });
            fetchUsers();
        } catch (err) {
            console.error("Error resetting user profile:", err);
            setError(err.message || "Could not reset user profile.");
        }
    };

    const openResetBudgetModal = (user) => {
        setUserToReset(user);
        setIsResetConfirmOpen(true);
    };

    const handleResetBudgetConfirm = async () => {
        if (!userToReset) return;
        try {
            const resetUserData = httpsCallable(functions, 'resetUserData');
            await resetUserData({ targetUid: userToReset.uid });
            alert(`Budget data for ${userToReset.email} has been reset.`);
        } catch (err) {
            setError(err.message || "Could not reset budget data.");
        } finally {
            setIsResetConfirmOpen(false);
            setUserToReset(null);
        }
    };

    return (
        <div className="max-w-4xl mx-auto p-4 md:p-8">
            <Header title="Super Admin" navigateTo={navigateTo} currentPage={currentPage} />
            <div className="mb-8">
                <button onClick={() => navigateTo('manageDefaultCategories')} className="w-full p-3 bg-cyan-500 text-white rounded-md hover:bg-cyan-600">
                    Manage Default Categories
                </button>
            </div>
            <div className="bg-white shadow-md rounded-lg p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">User Management</h2>
                {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
                {isLoading ? <p>Loading users...</p> : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Verified</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Super Admin</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reset Welcome</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reset Budget</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {users.map(user => (
                                    <tr key={user.uid}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            <div>{user.displayName || user.email}</div>
                                            <div className="text-xs text-gray-500">{user.uid}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {user.emailVerified ? (
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Yes</span>
                                            ) : (
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">No</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <label htmlFor={`admin-toggle-${user.uid}`} className="flex items-center cursor-pointer">
                                                <div className="relative">
                                                    <input type="checkbox" id={`admin-toggle-${user.uid}`} className="sr-only" checked={user.isSuperAdmin} onChange={() => handleAdminToggle(user.uid, user.isSuperAdmin)} />
                                                    <div className={`block w-10 h-6 rounded-full ${user.isSuperAdmin ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                                                    <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${user.isSuperAdmin ? 'transform translate-x-full' : ''}`}></div>
                                                </div>
                                            </label>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <button onClick={() => handleResetWelcome(user.uid)} className="text-indigo-600 hover:text-indigo-900 disabled:text-gray-400 disabled:cursor-not-allowed" disabled={!user.profileComplete}>Reset</button>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <button onClick={() => openResetBudgetModal(user)} className="text-red-600 hover:text-red-900">Reset</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            <ConfirmationModal 
                isOpen={isResetConfirmOpen} 
                onClose={() => setIsResetConfirmOpen(false)} 
                onConfirm={handleResetBudgetConfirm} 
                message={`Are you sure you want to delete all personal budget data (accounts and transactions) for ${userToReset?.email}? This action cannot be undone.`} 
            />
        </div>
    );
};

export default SuperAdminPage;
